import DeviceManager, { onPromiseAttrType } from '../utils/device-manager';
import { uniqid } from '../utils/utils';

export default function initCollapsibles() : void {
    const $collapsibleContainers = document.querySelectorAll<HTMLElement>('.js-collapsible-container');

    $collapsibleContainers.forEach($container => {
        new Collapsible($container);
    });
}

type CollapsibleElement = {$toggler: HTMLElement, $target: HTMLElement, $focusables: HTMLElement[]};

/**
 * Make elements collapsibles
 * 
 * Usage:
 * <div
 *   class="js-collapsible-container"
 *   data-collapsible-breakpoint-gte="xx" // Optional, initialize only if the breakpoint is greater than equal (sm, md, lg, xl)
 *   data-collapsible-breakpoint-lt="xx"  // Optional, initialize only if the breakpoint is lower than (sm, md, lg, xl)
 *   data-collapsible-auto-open-first-element="true" // Optional, open the first element at initialisation
 * >
 *   <button data-collapsible-toggler="menu">Toggle menu</button>
 *   <ul data-collapsible-target="menu">
 *     <li>Menu item</li>
 *   </ul>
 * </div>
 */
export class Collapsible {
    id = uniqid();
    $container: HTMLElement;
    elements: CollapsibleElement[] = [];
    breakpointGte: string|null;
    breakpointLt: string|null;
    autoOpenFirstElement: string|null;
    initialized = false;

    constructor($container: HTMLElement) {
        this.$container = $container;
        this.breakpointGte = this.$container.getAttribute('data-collapsible-breakpoint-gte') || null;
        this.breakpointLt = this.$container.getAttribute('data-collapsible-breakpoint-lt') || null;
        this.autoOpenFirstElement = this.$container.getAttribute('data-collapsible-auto-open-first-element') || null;

        // If the breakpoints doesn't exists, set to null
        if (this.breakpointGte && typeof DeviceManager.devices[this.breakpointGte] === 'undefined') {
            this.breakpointGte = null;
        }
        if (this.breakpointLt && typeof DeviceManager.devices[this.breakpointLt] === 'undefined') {
            this.breakpointLt = null;
        }

        DeviceManager.addCallback((attr: onPromiseAttrType) => {
            if (
                !this.initialized && ( // Is not intiialized
                    ( // Breakpoints not defined
                        !this.breakpointGte && !this.breakpointLt
                    ) ||
                    ( // Breakpoints defined and current is between them
                        this.breakpointGte && this.breakpointLt &&
                        DeviceManager.isBetween(attr.current, this.breakpointGte, this.breakpointLt)
                    ) ||
                    ( // Breakpoint GTE defined and current is GTE it
                        this.breakpointGte && !this.breakpointLt &&
                        DeviceManager.isGreaterThanOrEqual(attr.current, this.breakpointGte)
                    ) ||
                    ( // Breakpoint LT defined and current is LT it
                        !this.breakpointGte && this.breakpointLt &&
                        DeviceManager.isLowerThan(attr.current, this.breakpointLt)
                    )
                )
            ) {
                this.initialized = true;

                this.init();
            } else if (
                // Is initialized and breakpoints defined
                this.initialized && (this.breakpointGte || this.breakpointLt) && (
                    ( // Breakpoints defined and current is not between them
                        this.breakpointGte && this.breakpointLt &&
                        !DeviceManager.isBetween(attr.current, this.breakpointGte, this.breakpointLt)
                    ) ||
                    ( // Breakpoint GTE defined and current is not GTE it
                        this.breakpointGte && !this.breakpointLt &&
                        !DeviceManager.isGreaterThanOrEqual(attr.current, this.breakpointGte)
                    ) ||
                    ( // Breakpoint LT defined and current is not LT it
                        !this.breakpointGte && this.breakpointLt &&
                        !DeviceManager.isLowerThan(attr.current, this.breakpointLt)
                    )
                )
            ) {
                this.initialized = false;

                this.destroy();
            }
        });
    }

    /**
     * Initialize the collapsible elements
     * 
     * @return {void}
     */
    init() : void {
        this.$container.querySelectorAll<HTMLElement>('[data-collapsible-toggler]').forEach($toggler => {
            const targetId = $toggler.dataset.collapsibleToggler;
            const $target = this.$container.querySelector<HTMLElement>(`[data-collapsible-target="${targetId}"]`);

            if (!$target) {
                console.error(`Targetted element "[data-collapsible-target=${targetId}]" doesn't exists.`, $toggler);
                return;
            }

            // Register all focusable elements
            const focusableSelectors = ['a', 'button', '[tabindex]:not([tabindex="-1"])'].map(selector => selector + ':not([tabindex="-2"])').join(',');
            const $focusables = Array.from($target.querySelectorAll<HTMLElement>(focusableSelectors));

            // Populate the elements array
            this.elements.push({
                $toggler,
                $target,
                $focusables,
            });
        });

        // Apply classes to elements
        this.applyClasses();

        // Open the first collapsible target
        if (this.autoOpenFirstElement !== null) {
            this.open(this.elements[0].$toggler);
        }

        // Waiting 500ms for the styles to be applied before setting height of all collapsible targets
        setTimeout(() => {
            this.updateHeight();
        }, 500);

        // Bind event on collapsible elements
        this.elements.forEach(element => {
            if (element.$toggler.classList.contains('collapsible-event')) {
                // Do not bind the event if it already binded
                return;
            }

            // Add a class to not bind the event twice
            element.$toggler.classList.add('collapsible-event');
            element.$toggler.addEventListener('click', () => {
                if (!this.initialized) {
                    // Do not execute this event if not initialized
                    return;
                }

                // Open the element target if not already opened
                // Otherwise, close the element target
                if (element.$toggler.classList.contains('collapsible-toggler-hidden')) {
                    this.open(element.$toggler);
                } else {
                    this.close(element.$toggler);
                }
            });

            element.$toggler.addEventListener('keydown', (evt) => {
                if (evt.key !== 'Enter') {
                    return;
                }

                element.$toggler.click();
            });
        });

        // Update height of each collapsible targets on window resize
        DeviceManager.addWindowCallback('collapsible-' + this.id, () => {
            this.updateHeight();
        });
    }

    /**
     * Destroy the collapsible elements
     * 
     * @return {void}
     */
    destroy() : void {
        // Re-enable the focus on all focusables elements inside the collapsible target
        this.elements.forEach(element => {
            this.enableFocus(element.$focusables);
        });

        // Stop updating height of each collapsible targets on window resize
        DeviceManager.removeWindowCallback('collapsible-' + this.id);

        this.removeClasses();
    }

    /**
     * Apply classes on elements
     * 
     * @return {void}
     */
    applyClasses() : void {
        this.$container.classList.add('collapsible-container');

        this.elements.forEach(element => {
            element.$toggler.classList.add('collapsible-toggler', 'collapsible-toggler-hidden');
            element.$target.classList.add('collapsible-target', 'collapsible-target-hidden');
        });
    }

    /**
     * Remove classes on elements
     * 
     * @return {void}
     */
    removeClasses() : void {
        this.$container.classList.remove('collapsible-container');

        this.elements.forEach(element => {
            element.$toggler.classList.remove('collapsible-toggler', 'collapsible-toggler-hidden');
            element.$target.classList.remove('collapsible-target', 'collapsible-target-hidden');
        });
    }

    /**
     * Open a collapsible target
     * 
     * @param {HTMLElement} $toggler
     * 
     * @return {void}
     */
    open($toggler: HTMLElement) : void {
        this.elements.forEach(element => {
            if (element.$toggler === $toggler) {
                element.$toggler.classList.remove('collapsible-toggler-hidden');
                element.$target.classList.remove('collapsible-target-hidden');

                // Re-enable the focus on all focusables elements inside the collapsible target
                this.enableFocus(element.$focusables);
            } else {
                this.close(element);
            }
        });
    }

    /**
     * Close a collapsible target
     * 
     * @param {HTMLElement|CollapsibleElement} element
     * 
     * @return {void}
     */
    close(element: HTMLElement|CollapsibleElement) : void {
        if (element instanceof HTMLElement) {
            element = this.elements.filter(el => el.$toggler === element)[0];
        }

        element.$toggler.classList.add('collapsible-toggler-hidden');
        element.$target.classList.add('collapsible-target-hidden');

        // Disable the focus on all focusables elements inside the collapsible target
        this.disableFocus(element.$focusables);
    }

    /**
     * Enable the focus on focusables elements
     * 
     * @param {HTMLElement[]} $focusables
     * 
     * @return {void}
     */
    enableFocus($focusables: HTMLElement[]) : void {
        $focusables.forEach($focusable => {
            // Do not touch to links with tabindex different than "-2"
            if ($focusable.getAttribute('tabindex') !== '-2') {
                return;
            }

            const tabIndex = $focusable.getAttribute('data-tabindex');

            // If a tabindex is stored into data-tabindex, reset this tabindex value
            // Otherwise, remove the tabindex
            if (tabIndex) {
                $focusable.setAttribute('tabindex', tabIndex);
                $focusable.removeAttribute('data-tabindex');
            } else {
                $focusable.removeAttribute('tabindex');
            }
        });
    }

    /**
     * Disable the focus on focusables elements
     * 
     * @param {HTMLElement[]} $focusables
     * 
     * @return {void}
     */
    disableFocus($focusables: HTMLElement[]) : void {
        $focusables.forEach($focusable => {
            const tabIndex = $focusable.getAttribute('tabindex');

            // If the tabindex is different than "-2",
            // store the tabindex value into data-tabindex
            if (tabIndex !== '-2') {
                $focusable.setAttribute('data-tabindex', tabIndex);
            }

            // Set the tabindex to "-2"
            $focusable.setAttribute('tabindex', '-2');
        });
    }

    /**
     * Update height of all collapsible targets
     * 
     * @return {void}
     */
    updateHeight() : void {
        this.elements.forEach(element => {
            const isHidden = element.$target.classList.contains('collapsible-target-hidden');

            // Remove the maxHeight and transition
            element.$target.style.maxHeight = '';
            element.$target.style.transition = 'none';

            // If target element is hidden, show it
            if (isHidden) {
                element.$target.classList.remove('collapsible-target-hidden');
            }

            // Set the maxHeight with the real height of the target element
            const targetHeight = element.$target.clientHeight;
            element.$target.style.maxHeight = targetHeight + 'px';

            // If target element was hidden, re-hide it
            if (isHidden) {
                element.$target.classList.add('collapsible-target-hidden');
            }

            // Reset the transition
            setTimeout(() => {
                element.$target.style.transition = '';
            }, 1);
        });
    }
}
